import React, { useState } from "react";
import { ItemUI, createNewItem } from "../types/item";
import { User } from "../types/user";
import { AnimatePresence, MotionConfig } from "framer-motion";
import AddItemIcon from "../components/icons/AddItemIcon";
import InitItemRow from "./InitItemRow";
import SplitItemRow from "./SplitItemRow";
import "./ItemList.scss";

interface ItemListProps {
  items: ItemUI[];
  setItems: React.Dispatch<React.SetStateAction<ItemUI[]>>;
  selectedUserIndex: number | null;
  users: User[];
  // "init": building the check (/) — add/edit/delete items.
  // "split": the shared check (/:id) — claim and split items.
  mode: "init" | "split";
  // the split count (denominator) — anyone can change it
  canEditSplits: boolean;
  // whether the selected user's shares can be edited (true whenever a mate
  // is selected)
  canEditShares: boolean;
  // multi-select for the bulk editor (creator only)
  selectable: boolean;
  selectedItems: string[];
  onToggleSelect: (itemId: string) => void;
  // when set (shared receipt), share/splits edits persist instead of staying local
  onSetShare?: (itemId: string, userId: string, shares: number) => void;
  onSetSplits?: (itemId: string, splits: number) => void;
}

const ItemList: React.FC<ItemListProps> = ({
  items,
  setItems,
  selectedUserIndex,
  users,
  mode,
  canEditSplits,
  canEditShares,
  selectable,
  selectedItems,
  onToggleSelect,
  onSetShare,
  onSetSplits,
}) => {
  // for showing who holds each item's portions (split controls modal)
  const usersById = React.useMemo(
    () => new Map(users.map((u) => [u.id, u])),
    [users],
  );

  const selectedUser: User | null =
    selectedUserIndex === null ? null : users[selectedUserIndex];

  const setItem = (newItem: ItemUI) => {
    setItems((prev) => prev.map((it) => (it.id === newItem.id ? newItem : it)));
  };

  // set the selected user's shares of an item (0 removes them). Optimistic
  // local update; persist when on a shared receipt.
  const handleSetShare = (itemId: string, shares: number) => {
    if (selectedUser === null) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const next = { ...item.shares };
        if (shares > 0) next[selectedUser.id] = shares;
        else delete next[selectedUser.id];
        return { ...item, shares: next };
      }),
    );
    if (onSetShare) onSetShare(itemId, selectedUser.id, shares);
  };

  // set an item's split count; cap any share that now exceeds it (mirrors the
  // backend so a snapshot doesn't bounce the value)
  const handleSetSplits = (itemId: string, splits: number) => {
    const s = Math.max(1, splits);
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const next: Record<string, number> = {};
        for (const [u, n] of Object.entries(item.shares)) {
          const capped = Math.min(n, s);
          if (capped > 0) next[u] = capped;
        }
        return { ...item, splits: s, shares: next };
      }),
    );
    if (onSetSplits) onSetSplits(itemId, s);
  };

  // one tap on the pill: give the selected user one open portion (nothing if
  // the item is fully claimed — it never adds a split), or drop their
  // portions. Sent as the resulting absolute count through handleSetShare (not
  // as "+1" / "drop"), so rapid taps coalescing into one debounced write still
  // land on the right number.
  const handleQuickClaim = (itemId: string, claim: boolean) => {
    if (selectedUser === null) return;
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    const mine = item.shares[selectedUser.id] ?? 0;
    const total = Object.values(item.shares).reduce((a, b) => a + b, 0);
    const remaining = item.splits - total;
    const target = claim ? (remaining > 0 ? mine + 1 : mine) : 0;
    if (target !== mine) handleSetShare(itemId, target);
  };

  // a freshly added item opens straight into the edit modal — a blank row is
  // useless until it has a name and price
  const [newItemId, setNewItemId] = useState<string | null>(null);

  const handleAddAfter = (index: number) => {
    const newItem = createNewItem("", 0);
    const newItems = [...items];
    newItems.splice(index + 1, 0, newItem);
    setItems(newItems);
    setNewItemId(newItem.id);
  };

  // per-row delete — only offered pre-share, when items are still local
  const handleDelete = (itemId: string) => {
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  };

  const handleAddFirst = () => {
    const newItem = createNewItem("", 0);
    setItems((prev) => [newItem, ...prev]);
    setNewItemId(newItem.id);
  };

  return (
    // reducedMotion="user": skip the row animations for anyone who's asked
    // their OS to reduce motion
    <MotionConfig reducedMotion="user">
      <div className="item-list">
        {/* keeps a removed row mounted until its exit animation finishes;
          initial={false} so the rows don't all grow in on page load. Keyed by
          mode so sharing (init -> split) remounts it, swapping every row's
          variant without each one animating in. */}
        <AnimatePresence key={mode} initial={false}>
          {items.map((item, index) =>
            mode === "init" ? (
              <InitItemRow
                key={item.id}
                item={item}
                setItem={setItem}
                onAddFirst={index === 0 ? handleAddFirst : undefined}
                onAddAfter={() => handleAddAfter(index)}
                onDelete={() => handleDelete(item.id)}
                autoEdit={item.id === newItemId}
                onAutoEditDone={() => setNewItemId(null)}
                selected={selectedItems.includes(item.id)}
                onToggleSelect={
                  selectable ? () => onToggleSelect(item.id) : undefined
                }
              />
            ) : (
              <SplitItemRow
                key={item.id}
                item={item}
                selectedUser={selectedUser}
                usersById={usersById}
                canEditShares={canEditShares}
                canEditSplits={canEditSplits}
                onSetShare={(n) => handleSetShare(item.id, n)}
                onSetSplits={(n) => handleSetSplits(item.id, n)}
                onQuickClaim={(claim) => handleQuickClaim(item.id, claim)}
                selected={selectedItems.includes(item.id)}
                onToggleSelect={() => onToggleSelect(item.id)}
              />
            ),
          )}
        </AnimatePresence>
        {items.length === 0 && mode === "init" && (
          <AddItemIcon onClick={handleAddFirst} />
        )}
      </div>
    </MotionConfig>
  );
};

export default ItemList;
